import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback} from 'react'

import type {IssueCreateContext} from '../utils/option-config'

const ANALYTICS_TARGET = 'SIMILAR_ISSUES_ISSUE_CREATION'

export const SimilarIssuesAnalyticsEvent = {
  SectionShown: 'issue_create.similar_issues.section_shown',
  SectionExpanded: 'issue_create.similar_issues.section_expanded',
  ResultsShown: 'issue_create.similar_issues.results_shown',
  IssueClicked: 'issue_create.similar_issues.issue_clicked',
  IssueCreated: 'issue_create.similar_issues.issue_created',
  RequestMade: 'issue_create.similar_issues.semantic_search_request_made',
} as const

type IssueCreatedPayload = {
  hadResults: boolean
  wasSectionExpanded: boolean
  wasIssueClicked: boolean
  issueClickCount: number
}

/**
 * Hook for sending similar issues analytics events.
 * Session ID is passed at call time to ensure callbacks are stable and always use the current value.
 *
 * @param context - The context in which the issue is being created
 * @param repoNWO - The repository name with owner (e.g. "owner/repo") to explicitly attach to events
 */
export function useSimilarIssuesAnalytics(context: IssueCreateContext, repoNWO: string) {
  const {sendAnalyticsEvent} = useAnalytics()

  /** Sends an analytics event with the session ID and context. Additional metadata can be provided. */
  const sendSimilarIssuesEvent = useCallback(
    (event: string, sessionId: string, extra?: Record<string, unknown>) => {
      if (!sessionId) return
      sendAnalyticsEvent(event, ANALYTICS_TARGET, {
        issueCreateSessionId: sessionId,
        issueCreateContext: context,
        repoNWO,
        ...extra,
      })
    },
    [sendAnalyticsEvent, context, repoNWO],
  )

  /** Tracks when API call is made to the similar issues endpoint */
  const sendRequestMade = useCallback(
    (sessionId: string, title: string, body: string) =>
      sendSimilarIssuesEvent(SimilarIssuesAnalyticsEvent.RequestMade, sessionId, {
        titleLength: title.length,
        bodyLength: body.length,
      }),
    [sendSimilarIssuesEvent],
  )

  /** Tracks feature availability. Denominator for whether users expand this section: section_expanded / section_shown */
  const sendSectionShown = useCallback(
    (sessionId: string) => sendSimilarIssuesEvent(SimilarIssuesAnalyticsEvent.SectionShown, sessionId),
    [sendSimilarIssuesEvent],
  )

  /** Tracks whether user expands the section or ignores it. */
  const sendSectionExpanded = useCallback(
    (sessionId: string) => sendSimilarIssuesEvent(SimilarIssuesAnalyticsEvent.SectionExpanded, sessionId),
    [sendSimilarIssuesEvent],
  )

  /** Tracks whether non-empty results were shown at least once in the session. */
  const sendResultsShown = useCallback(
    (sessionId: string) => sendSimilarIssuesEvent(SimilarIssuesAnalyticsEvent.ResultsShown, sessionId),
    [sendSimilarIssuesEvent],
  )

  /** Tracks whether users interact with results, along with position of the clicked item. */
  const sendIssueClicked = useCallback(
    (sessionId: string, position: number) =>
      sendSimilarIssuesEvent(SimilarIssuesAnalyticsEvent.IssueClicked, sessionId, {position}),
    [sendSimilarIssuesEvent],
  )

  /** Determine whether issues were created, along with analysis of behavior leading to issue creation. */
  const sendIssueCreated = useCallback(
    (sessionId: string, payload: IssueCreatedPayload) =>
      sendSimilarIssuesEvent(SimilarIssuesAnalyticsEvent.IssueCreated, sessionId, {
        hadNonEmptyResults: payload.hadResults,
        wasSectionExpanded: payload.wasSectionExpanded,
        wasIssueClicked: payload.wasIssueClicked,
        issueClickCount: payload.issueClickCount,
      }),
    [sendSimilarIssuesEvent],
  )

  return {
    sendSectionShown,
    sendSectionExpanded,
    sendResultsShown,
    sendIssueClicked,
    sendIssueCreated,
    sendRequestMade,
  }
}
